import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ticket, Loader2 } from "lucide-react";

const empty = { code: "", discount_type: "percent", discount_value: "", min_order: "", is_active: true, expires_at: "" };

export default function AdminCoupons() {
  usePageMeta("Manage Coupons");
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/coupons").then(({ data }) => setCoupons(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ code: c.code, discount_type: c.discount_type, discount_value: c.discount_value, min_order: c.min_order || "", is_active: c.is_active, expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.discount_value) { toast.error("Code and discount value are required"); return; }
    setSaving(true);
    const payload = {
      code: form.code, discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value), min_order: parseFloat(form.min_order || 0),
      is_active: form.is_active,
      expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
    };
    try {
      if (editing) await api.put(`/admin/coupons/${editing.id}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success(editing ? "Coupon updated" : "Coupon created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"?`)) return;
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Coupons</h1>
          <p className="mt-1 text-ink-soft">Create discount codes for your customers.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="add-coupon-btn"><Plus className="mr-1 h-4 w-4" /> Add Coupon</Button>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : coupons.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <Ticket className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-4 font-heading text-lg text-ink">No coupons yet</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {coupons.map((c) => (
            <div key={c.id} className="rounded-2xl border border-border/60 bg-white p-5 shadow-ambient" data-testid={`admin-coupon-${c.code}`}>
              <div className="flex items-start justify-between">
                <span className="rounded-lg border border-dashed border-brand bg-brand/5 px-3 py-1 font-mono font-bold text-brand">{c.code}</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${c.is_active ? "bg-sage/15 text-sage" : "bg-ink/10 text-ink-muted"}`}>{c.is_active ? "Active" : "Off"}</span>
              </div>
              <p className="mt-3 text-lg font-semibold text-ink">
                {c.discount_type === "percent" ? `${c.discount_value}% off` : `${rupee(c.discount_value)} off`}
              </p>
              <p className="text-xs text-ink-muted">{c.min_order > 0 ? `Min order ${rupee(c.min_order)}` : "No minimum"}{c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString("en-IN")}` : ""}</p>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(c)} className="rounded-full"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => remove(c)} className="rounded-full text-destructive"><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Coupon" : "Add Coupon"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1.5 rounded-xl font-mono" placeholder="FRESH10" data-testid="coupon-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage</SelectItem>
                    <SelectItem value="flat">Flat (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.discount_type === "percent" ? "Percent (%)" : "Amount (₹)"}</Label>
                <Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="mt-1.5 rounded-xl" data-testid="coupon-value-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min order (₹)</Label>
                <Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="mt-1.5 rounded-xl" placeholder="0" />
              </div>
              <div>
                <Label>Expires (optional)</Label>
                <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1.5 rounded-xl" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-cream-alt/50 px-4 py-3">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-brand text-ink" data-testid="save-coupon-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
